import { Circle, Delete, PersonAdd } from '@mui/icons-material'
import ArrowForward from '@mui/icons-material/ArrowForward'
import {
	Button,
	Chip,
	IconButton,
	TableCell
} from '@mui/material'
import { green, red, yellow } from '@mui/material/colors'
import { action, runInAction } from 'mobx'
import { observer } from 'mobx-react'
import { useLocalObservable } from 'mobx-react-lite'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useRouteMatch } from 'react-router'
import { BaseUser, UserType } from '../../../../../api/DTO'
import { AccessStatus } from '../../../../../api/DTOtemp'
import { CurrentOrganizationUsersClient } from '../../../../../api/clients/identity/CurrentOrganizationUsersClient'
import { DeleteDialog } from '../../../../../components/dialogs/DeleteDialog'
import { FullscreenSpinner } from '../../../../../components/feedback/circular'
import IconButtonLink from '../../../../../components/links/IconButtonLink'
import { TableColumn } from '../../../../../components/tables/DTO'
import { FilteredTable } from '../../../../../components/tables/FilteredTable'
import { FilterControl } from '../../../../../components/tables/filters/DTO'
import { SessionService } from '../../../../../services/session'
import { makeChildRoute } from '../../../../../utils/CreateChildRoute'
import AdministrationPageContainer from '../../AdministrationPageContainer'
import { UserLogSection } from '../UserLogSection'
import CreateUserDialog from './CreateUserDialog'

const UsersPage = observer(() => {
	const store = useLocalObservable(() => ({
		isFetching: false,
		users: undefined as BaseUser[] | undefined,

		createUserOpen: false,
		userToRemove: undefined as BaseUser | undefined,
	}))

	const [selectedTabIndex, setSelectedTabIndex] = useState(0)

	const api = new CurrentOrganizationUsersClient()

	const handleShowCreateUser = useCallback(
		action(() => {
			store.createUserOpen = true
		}),
		[store],
	)

	const handleHideCreateUser = useCallback(
		action(() => {
			store.createUserOpen = false
		}),
		[store],
	)

	const handleShowDeleteAssociation = useCallback(
		action((user: BaseUser) => {
			store.userToRemove = user
		}),
		[store],
	)

	const handleHideDeleteAssociation = useCallback(
		action(() => {
			store.userToRemove = undefined
		}),
		[store],
	)

	const handleDeleteAssociation = useCallback(
		(userId: number) => {
			api.deleteAssociationForCurrentOrganization(userId)
			runInAction(() => {
				if (store.users !== undefined) {
					const userToRemove = store.users.map((v) => v.id).indexOf(userId)
					store.users.splice(userToRemove, 1)
				}
			})
		},
		[store],
	)

	const getAccessStatusText = (accessStatus: AccessStatus): string => {
		switch (accessStatus) {
			case 0:
				return 'Awaiting Approval'
			case 1:
				return 'Locked Out'
			case 2:
				return 'Access Permitted'
			default:
				return ''
		}
	}
	const sessionService = SessionService.getInstance()
	const { url } = useRouteMatch()

	const renderAccessStatus = (status: AccessStatus) =>
		<Chip
			icon={
				<div>
					<Circle
						sx={{
							color:
								status === AccessStatus.AccessPermitted
									? green[500]
									: status === AccessStatus.LockedOut
										? red[500]
										: yellow[500],
							fontSize: 10,
						}}
					/>
				</div>
			}
			label={getAccessStatusText(status)}
			variant="outlined"
		/>

	const filterControls: FilterControl[] = useMemo(() => [
		{
			type: 'date range',
			name: 'createdDate',
			label: 'Created Date'
		},
		{
			type: 'multiselect',
			name: 'accessStatus',
			label: 'Access Status',
			options: Object.keys(AccessStatus).filter(key => !isNaN(parseInt(key))).map((key) => ({ label: renderAccessStatus(Number(key)), value: Number(key) }))
		},
		{
			type: 'search',
			name: 'search',
			column: ["firstName", "lastName", "emailAddress"]
		}
	], [store.users])

	const columns: TableColumn<BaseUser>[] = useMemo(() => [
		{
			name: "firstName",
			label: "First Name"
		},
		{
			name: "lastName",
			label: "Last Name"
		},
		{
			name: "emailAddress",
			label: "Email Address"
		},
		{
			name: "createdDate",
			label: "Created Date",
			renderContent: (user) => new Date(user.createdDate).getUTCFullYear() === 1 ? "-" : new Date(user.createdDate).toLocaleString()
		},
		{
			name: "accessStatus",
			label: "Access Status",
			renderContent: (user) => renderAccessStatus(user.accessStatus)
		},
		{
			renderHeader: <TableCell align="right">Actions</TableCell>,
			renderCell: (user) =>
				<TableCell key={5} align="right">
					{sessionService.userId !== user.id &&
						user.type !== UserType.AuthProvider ? (
						<IconButton onClick={() => handleShowDeleteAssociation(user)}>
							<Delete fontSize="small" />
						</IconButton>
					) : null}

					<IconButtonLink to={makeChildRoute(user.id.toString(), url)}>
						<ArrowForward fontSize="small" />
					</IconButtonLink >
				</TableCell>
		},
	], [])

	const tabSelector = () => {
		if (selectedTabIndex === 0)
			return store.users === undefined ? <FullscreenSpinner /> : (
				<FilteredTable
					allEntries={store.users}
					columns={columns}
					filterControls={filterControls}
				/>
			)
		if (selectedTabIndex === 1)
			return <UserLogSection />
	}

	useEffect(() => {
		store.isFetching = true
		api.getUsersForCurrentOrganization(undefined).then(
			action((v) => {
				store.isFetching = false
				store.users = v.data
			}),
		)
	}, [store])

	return (
		<>
			<AdministrationPageContainer
				title="Users"
				actions={
					<div>
						<Button
							variant="contained"
							color="primary"
							startIcon={<PersonAdd />}
							onClick={handleShowCreateUser}
						>
							Create User
						</Button>
					</div>
				}
				tabs={["List", "Logs"]}
				onTabChanged={setSelectedTabIndex}

			>
				{tabSelector()}
			</AdministrationPageContainer>
			<CreateUserDialog
				open={store.createUserOpen}
				onExit={handleHideCreateUser}
				onUserCreated={handleHideCreateUser}
			/>
			{store.userToRemove !== undefined && (
				<DeleteDialog<number>
					open={store.userToRemove !== undefined}
					itemName={`${store.userToRemove.firstName} ${store.userToRemove.lastName}`}
					itemId={store.userToRemove.id}
					onClose={handleHideDeleteAssociation}
					onDelete={handleDeleteAssociation}
				/>
			)}
		</>
	)
})

export default UsersPage